import React, { useState, useEffect } from "react";
import axios from "axios";
import { backendURL } from "../config";
import {
    Crosshair, Search, Play, CheckCircle2, Clock, Shield, AlertTriangle,
    ChevronDown, ChevronRight, Globe, Server, Activity, Zap, Target,
    FileText, ArrowRight, Loader2, X, Bot, ShieldAlert, ShieldCheck, Eye, Plus,
    RefreshCw, Sparkles, ChevronLeft, User, Terminal, Check, Share2
} from "lucide-react";

const verdictBadges = {
    "Confirmed Threat":   { bg: "bg-rose-500/15", border: "border-rose-500/30", text: "text-rose-400" },
    "Likely Threat":      { bg: "bg-orange-500/15", border: "border-orange-500/30", text: "text-orange-400" },
    "Suspicious Activity":{ bg: "bg-amber-500/15", border: "border-amber-500/30", text: "text-amber-400" },
    "Likely Benign":      { bg: "bg-emerald-500/15", border: "border-emerald-500/30", text: "text-emerald-400" },
    "No Evidence Found":  { bg: "bg-slate-500/15", border: "border-slate-500/30", text: "text-slate-400" },
};

export default function ThreatHunt() {
    const [hunts, setHunts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Execution Modal / Drawer State
    const [activeHuntModal, setActiveHuntModal] = useState(null);
    const [isExecuting, setIsExecuting] = useState(false);
    const [liveSteps, setLiveSteps] = useState([]);
    const [huntResult, setHuntResult] = useState(null);
    const [activeTab, setActiveTab] = useState("verdict"); // verdict | evidence | query

    // Case Creation State from Hunt
    const [creatingCase, setCreatingCase] = useState(false);
    const [caseCreatedSuccess, setCaseCreatedSuccess] = useState(null);

    // Add Hunt Modal State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addForm, setAddForm] = useState({ name: "", description: "", targetType: "Zero-Day Vulnerability" });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const handleCreateCaseFromHunt = async () => {
        if (!huntResult) return;
        setCreatingCase(true);
        try {
            const payload = {
                title: activeHuntModal?.name || huntResult.prompt || "Threat Hunt Correlated Threat",
                severity: (huntResult.verdict?.verdict || "").toLowerCase().includes("confirmed") ? "CRITICAL" : "HIGH",
                categories: ["THREAT HUNT", "IPS", "C2"],
                description: huntResult.verdict?.summary || `Elevated threat detected via autonomous hunt with ${huntResult.findings?.matchCount} matching events across ${huntResult.findings?.uniqueSourceIPs?.length} endpoints.`,
                assignee: localStorage.getItem("userName") || "Musaab Syed"
            };
            const res = await axios.post(`${backendURL}/api/cases`, payload, { headers });
            if (res.data.success) {
                setCaseCreatedSuccess(res.data.case?.id || "Case Created");
                setTimeout(() => setCaseCreatedSuccess(null), 5000);
            }
        } catch (err) {
            console.error("Failed to create case from hunt:", err);
        } finally {
            setCreatingCase(false);
        }
    };

    useEffect(() => {
        fetchHunts();
    }, []);

    const fetchHunts = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/hunt/library`, { headers });
            setHunts(res.data.hunts || []);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to fetch hunt library:", err);
        }
        setLoading(false);
    };

    const runHunt = async (hunt) => {
        setActiveHuntModal(hunt);
        setIsExecuting(true);
        setHuntResult(null);
        setLiveSteps([]);

        const stepLabels = [
            "Parsing hunt hypothesis & indicators...",
            "Querying 500k+ perimeter & endpoint logs...",
            "Correlating incident patterns & MITRE techniques...",
            "Aggregating entity findings & IP matches...",
            "QueryTel generating threat verdict..."
        ];

        for (let i = 0; i < stepLabels.length; i++) {
            await new Promise(r => setTimeout(r, 350 + Math.random() * 250));
            setLiveSteps(prev => [...prev, { step: i + 1, label: stepLabels[i], status: "complete", duration: `${40 + i * 15}ms` }]);
        }

        try {
            const res = await axios.post(`${backendURL}/api/hunt`, { prompt: hunt.description || hunt.name }, { headers });
            setHuntResult(res.data);
            await fetchHunts();
        } catch (err) {
            console.error("Hunt execution failed:", err);
        }
        setIsExecuting(false);
    };

    const handleCreateHunt = async (e) => {
        e.preventDefault();
        if (!addForm.name || !addForm.description) return;

        setIsSubmitting(true);
        try {
            const res = await axios.post(`${backendURL}/api/hunt/library`, addForm, { headers });
            setIsAddModalOpen(false);
            setAddForm({ name: "", description: "", targetType: "Zero-Day Vulnerability" });
            await fetchHunts();
            if (res.data.hunt) {
                runHunt(res.data.hunt);
            }
        } catch (err) {
            console.error("Failed to create hunt:", err);
        }
        setIsSubmitting(false);
    };

    const formatDate = (isoStr) => {
        if (!isoStr) return "—";
        const d = new Date(isoStr);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    };

    // Filter Logic
    const filteredHunts = hunts.filter(h => {
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            return (
                h.name?.toLowerCase().includes(q) ||
                h.description?.toLowerCase().includes(q) ||
                h.createdBy?.toLowerCase().includes(q) ||
                h.verdict?.toLowerCase().includes(q) ||
                h.targetType?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    const totalPages = Math.max(1, Math.ceil(filteredHunts.length / pageSize));
    const paginatedHunts = filteredHunts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const startItem = filteredHunts.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, filteredHunts.length);

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Breadcrumb Header ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>04</span>
                <span>/</span>
                <span className="text-slate-400">Your Hunt Library</span>
            </div>

            {/* ─── Page Title & Main Banner ─── */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                    <h1 className="text-[24px] lg:text-[28px] font-extrabold text-white tracking-tight">
                        Every hunt, saved and repeatable.
                    </h1>
                    <p className="text-[13px] text-slate-400 mt-1 max-w-3xl leading-relaxed">
                        Hunts do not disappear when they finish. Each one is saved with its plan, its findings, and its verdict, so you can re-run it, share it, or build on it, from a zero-day CVE to a supply-chain package to a suspicious login pattern.
                    </p>
                </div>

                <div className="flex items-center gap-3 self-start lg:self-auto">
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 via-rose-600 to-indigo-600 hover:from-orange-500 hover:to-indigo-500 text-white rounded-lg text-[12px] font-bold shadow-lg shadow-orange-600/20 transition-all hover:scale-[1.02]"
                    >
                        <Plus className="w-4 h-4" />
                        Add Threat Hunt
                    </button>
                </div>
            </div>

            {/* ─── Search Bar ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-3.5 flex items-center justify-between gap-3 shadow-sm">
                <div className="flex items-center bg-black/30 border border-white/[0.08] rounded-lg px-3 py-1.5 w-full md:w-96">
                    <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                        className="bg-transparent border-none outline-none text-[12px] text-slate-200 placeholder:text-slate-600 w-full"
                    />
                </div>

                <div className="text-[11px] text-slate-500 font-mono hidden sm:block">
                    Total <strong className="text-slate-300">{filteredHunts.length}</strong> Saved Playbooks
                </div>
            </div>

            {/* ─── Saved Hunt Library Table ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-[12px] border-collapse">
                        <thead>
                            <tr className="bg-black/30 border-b border-white/[0.06] text-slate-400 font-bold uppercase text-[10px] tracking-wider">
                                <th className="py-3.5 px-4 font-semibold">Name / Description</th>
                                <th className="py-3.5 px-4 font-semibold">Created Date</th>
                                <th className="py-3.5 px-4 font-semibold">Created By</th>
                                <th className="py-3.5 px-4 font-semibold text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.03]">
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="py-12 text-center text-slate-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-400" />
                                        Loading saved threat hunt library...
                                    </td>
                                </tr>
                            ) : paginatedHunts.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="py-12 text-center text-slate-500">
                                        <Crosshair className="w-8 h-8 mx-auto mb-2 text-slate-600 opacity-50" />
                                        No saved hunts match your search.
                                    </td>
                                </tr>
                            ) : (
                                paginatedHunts.map((hunt) => {
                                    const vStyle = verdictBadges[hunt.verdict] || verdictBadges["No Evidence Found"];

                                    return (
                                        <tr
                                            key={hunt.huntId}
                                            onClick={() => runHunt(hunt)}
                                            className="cursor-pointer transition-all hover:bg-white/[0.02] group"
                                        >
                                            {/* Name / Description */}
                                            <td className="py-3.5 px-4 max-w-md">
                                                <div className="space-y-0.5">
                                                    <span className="font-bold text-slate-100 group-hover:text-indigo-400 transition-colors leading-snug line-clamp-1">
                                                        {hunt.name}
                                                    </span>
                                                    <p className="text-[11px] text-slate-400 line-clamp-1">
                                                        {hunt.description}
                                                    </p>
                                                </div>
                                            </td>

                                            {/* Created Date */}
                                            <td className="py-3.5 px-4 text-slate-400 text-[11px] whitespace-nowrap">
                                                {formatDate(hunt.createdDate)}
                                            </td>

                                            {/* Created By */}
                                            <td className="py-3.5 px-4">
                                                <span className="inline-flex items-center gap-1.5 text-slate-300 font-medium text-[11px]">
                                                    <User className="w-3 h-3 text-slate-500" />
                                                    {hunt.createdBy}
                                                </span>
                                            </td>

                                            {/* Actions */}
                                            <td className="py-3.5 px-4 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center gap-2">
                                                    {hunt.verdict && (
                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${vStyle.bg} ${vStyle.border} ${vStyle.text}`}>
                                                            {hunt.verdict}
                                                        </span>
                                                    )}

                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); runHunt(hunt); }}
                                                        className="flex items-center gap-1 px-3 py-1 bg-white/[0.04] hover:bg-indigo-600 text-slate-300 hover:text-white rounded-md text-[11px] font-bold transition-all border border-white/[0.08]"
                                                    >
                                                        <Play className="w-3 h-3 fill-current" />
                                                        Re-run
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ─── Pager Toolbar ─── */}
                {totalPages > 1 && (
                    <div className="border-t border-white/[0.06] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-black/20 text-[12px] text-slate-400">
                        <div>
                            Showing <strong className="text-white font-mono">{startItem}</strong> to{" "}
                            <strong className="text-white font-mono">{endItem}</strong> of{" "}
                            <strong className="text-white font-mono">{filteredHunts.length}</strong> Threat Hunts
                        </div>

                        <div className="flex items-center gap-1.5">
                            {/* Prev Button */}
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 text-slate-300 rounded-md font-medium transition-all"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                Prev
                            </button>

                            {/* Page Number Pills */}
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className={`w-8 h-8 rounded-md font-mono text-[11px] font-bold transition-all ${
                                        currentPage === pageNum
                                            ? "bg-indigo-600 text-white shadow-sm"
                                            : "bg-white/[0.04] hover:bg-white/[0.08] text-slate-300"
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            ))}

                            {/* Next Button */}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 text-slate-300 rounded-md font-medium transition-all"
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ─── Live Hunt Execution & Findings Slide-Over Modal ─── */}
            {activeHuntModal && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="p-5 border-b border-white/[0.06] flex items-center justify-between bg-black/30">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-rose-600 flex items-center justify-center shadow-lg">
                                    <Crosshair className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-[15px] font-bold text-white leading-tight">{activeHuntModal.name}</h3>
                                        <span className="text-[10px] text-slate-500 font-mono">{activeHuntModal.huntId}</span>
                                    </div>
                                    <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{activeHuntModal.description}</p>
                                </div>
                            </div>

                            <button
                                onClick={() => setActiveHuntModal(null)}
                                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04]"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {/* Execution Step Flow */}
                            <div className="bg-black/30 border border-white/[0.04] rounded-xl p-4 space-y-2">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                        <Terminal className="w-3.5 h-3.5 text-indigo-400" />
                                        QueryTel Autonomous Telemetry Execution Steps
                                    </p>
                                    {isExecuting ? (
                                        <span className="text-[10px] text-indigo-400 flex items-center gap-1 font-mono">
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            Scanning 500k logs...
                                        </span>
                                    ) : (
                                        <span className="text-[10px] text-emerald-400 font-mono font-semibold">
                                            ✓ Completed in {huntResult?.totalDuration || 0}ms
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    {liveSteps.map((step, i) => (
                                        <div key={i} className="flex items-center justify-between text-[11px] bg-black/40 px-3 py-2 rounded-lg border border-white/[0.03]">
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                                <span className="text-slate-200">{step.label}</span>
                                            </div>
                                            <span className="text-slate-500 font-mono text-[10px]">{step.duration}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Verdict & Findings Summary */}
                            {huntResult && (
                                <div className="space-y-4">
                                    {/* Verdict Banner */}
                                    <div className={`p-4 rounded-xl border flex items-center justify-between ${
                                        huntResult.verdict?.verdict === "Confirmed Threat" ? "bg-rose-500/15 border-rose-500/30 text-rose-200" :
                                        huntResult.verdict?.verdict === "Likely Threat" ? "bg-orange-500/15 border-orange-500/30 text-orange-200" :
                                        huntResult.verdict?.verdict === "Suspicious Activity" ? "bg-amber-500/15 border-amber-500/30 text-amber-200" :
                                        "bg-emerald-500/15 border-emerald-500/30 text-emerald-200"
                                    }`}>
                                        <div>
                                            <p className="text-[10px] uppercase font-bold tracking-wider opacity-80">QueryTel Autonomous Verdict</p>
                                            <h4 className="text-[18px] font-extrabold mt-0.5">{huntResult.verdict?.verdict}</h4>
                                            <p className="text-[12px] opacity-90 mt-1">{huntResult.verdict?.summary}</p>
                                        </div>
                                        <div className="text-right font-mono">
                                            <span className="text-[28px] font-black">{huntResult.verdict?.confidence}%</span>
                                            <span className="block text-[10px] opacity-80 uppercase">Confidence</span>
                                        </div>
                                    </div>

                                    {/* Findings Stats */}
                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="p-3 bg-black/20 rounded-lg border border-white/[0.04]">
                                            <span className="text-[10px] text-slate-500 uppercase font-semibold">Matched Logs</span>
                                            <p className="text-[20px] font-black text-white mt-0.5">{huntResult.findings?.matchCount || 0}</p>
                                        </div>
                                        <div className="p-3 bg-black/20 rounded-lg border border-white/[0.04]">
                                            <span className="text-[10px] text-slate-500 uppercase font-semibold">Unique Source IPs</span>
                                            <p className="text-[20px] font-black text-indigo-400 mt-0.5">{huntResult.findings?.uniqueSourceIPs?.length || 0}</p>
                                        </div>
                                        <div className="p-3 bg-black/20 rounded-lg border border-white/[0.04]">
                                            <span className="text-[10px] text-slate-500 uppercase font-semibold">Related Incidents</span>
                                            <p className="text-[20px] font-black text-amber-400 mt-0.5">{huntResult.relatedIncidents?.length || 0}</p>
                                        </div>
                                    </div>

                                    {/* Matched Log Evidence */}
                                    {huntResult.findings?.sampleEvidence && huntResult.findings.sampleEvidence.length > 0 && (
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <h5 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Matched Telemetry Evidence</h5>
                                                <span className="text-[10px] text-slate-500">Showing latest {huntResult.findings.sampleEvidence.length} correlated events</span>
                                            </div>
                                            <div className="space-y-2 max-h-56 overflow-y-auto custom-scrollbar">
                                                {huntResult.findings.sampleEvidence.map((ev, i) => {
                                                    const isBlocked = ['dropped', 'deny', 'block', 'close', 'dropped-by-rule'].includes((ev.action || '').toLowerCase());
                                                    return (
                                                        <div key={i} className="p-3 bg-black/40 rounded-lg border border-white/[0.06] text-[11px] space-y-1.5 hover:border-white/[0.12] transition-colors">
                                                            <div className="flex items-center justify-between font-mono text-[10px]">
                                                                <span className="text-slate-400">{formatDate(ev.ts)}</span>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-rose-400 font-semibold px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/20">{ev.category}</span>
                                                                    {isBlocked ? (
                                                                        <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                                                                            <ShieldCheck className="w-3 h-3" /> FIREWALL BLOCKED ({ev.action?.toUpperCase()})
                                                                        </span>
                                                                    ) : (
                                                                        <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                                                                            <AlertTriangle className="w-3 h-3" /> TRAFFIC PASSED / MONITORED
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 font-mono text-slate-200">
                                                                <span className="text-blue-400 font-bold">{ev.sourceIp || "Internal LAN"}</span>
                                                                <ArrowRight className="w-3 h-3 text-slate-600" />
                                                                <span className="text-purple-400 font-bold">{ev.destIp || "External Host"}{ev.dstPort ? `:${ev.dstPort}` : ''}</span>
                                                                {ev.service && <span className="text-slate-500 text-[10px] font-sans">({ev.service})</span>}
                                                            </div>
                                                            {ev.attack && (
                                                                <div className="text-[10px] text-rose-300/90 font-mono bg-rose-500/5 px-2 py-1 rounded border border-rose-500/15">
                                                                    Attack Signature: <span className="text-rose-200 font-bold">{ev.attack}</span>
                                                                </div>
                                                            )}
                                                            <p className="text-[11px] text-slate-300 font-sans leading-relaxed">{ev.message}</p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-white/[0.06] bg-black/40 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => runHunt(activeHuntModal)}
                                    disabled={isExecuting}
                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-[12px] font-bold transition-all disabled:opacity-50 cursor-pointer"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${isExecuting ? "animate-spin" : ""}`} />
                                    Re-Scan Latest Telemetry
                                </button>

                                {huntResult && (huntResult.findings?.matchCount > 0 || (huntResult.verdict?.verdict || '').toLowerCase().includes('threat')) && (
                                    <button
                                        onClick={handleCreateCaseFromHunt}
                                        disabled={creatingCase || caseCreatedSuccess}
                                        className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-600/20 hover:bg-rose-600/30 border border-rose-500/40 text-rose-300 rounded-lg text-[12px] font-bold transition-all cursor-pointer disabled:opacity-60"
                                    >
                                        {creatingCase ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                        <span>{caseCreatedSuccess ? `Case Created: ${caseCreatedSuccess}` : "Escalate & Create Case"}</span>
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={() => setActiveHuntModal(null)}
                                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[12px] font-semibold transition-colors cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Add Threat Hunt Modal ─── */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-white/[0.06] flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 flex items-center justify-center shadow-lg">
                                    <Plus className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-[16px] font-bold text-white">Add Threat Hunt Playbook</h3>
                                    <p className="text-[11px] text-slate-400">Save a persistent, reusable hunt query for the whole SOC team.</p>
                                </div>
                            </div>

                            <button
                                onClick={() => setIsAddModalOpen(false)}
                                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/[0.04]"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Quick Templates */}
                        <div className="px-6 pt-4">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Quick Start Playbooks</p>
                            <div className="flex flex-wrap gap-1.5">
                                {[
                                    { name: "Zero-Day CVE Infiltration Sweep", desc: "Find exploit attempts against perimeter firewall endpoints and CVE indicators" },
                                    { name: "C2 DNS Tunneling & TXT Anomalies", desc: "Inspect high-frequency DNS query volume and long TXT record lookups" },
                                    { name: "Privilege Escalation via Scheduled Tasks", desc: "Detect administrative account creation and lateral movement over SMB/RDP" }
                                ].map((tmpl, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => setAddForm({ ...addForm, name: tmpl.name, description: tmpl.desc })}
                                        className="text-[11px] font-medium bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08] text-slate-300 px-2.5 py-1 rounded-md transition-all"
                                    >
                                        {tmpl.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <form onSubmit={handleCreateHunt} className="p-6 space-y-4">
                            <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-slate-300">Hunt Name / Title *</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g., Hunt for Okta/Slack Session Hijacking"
                                    value={addForm.name}
                                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-slate-300">Target Threat Type</label>
                                <select
                                    value={addForm.targetType}
                                    onChange={(e) => setAddForm({ ...addForm, targetType: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white outline-none focus:border-indigo-500"
                                >
                                    <option value="Zero-Day Vulnerability">Zero-Day Vulnerability</option>
                                    <option value="Identity & SSO">Identity & SSO</option>
                                    <option value="Supply Chain">Supply Chain</option>
                                    <option value="Endpoint / CLI">Endpoint / CLI</option>
                                    <option value="C2 Beaconing">C2 Beaconing</option>
                                    <option value="Phishing / Email">Phishing / Email</option>
                                    <option value="Network Exfiltration">Network Exfiltration</option>
                                </select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-slate-300">Hunt Hypothesis & Prompt *</label>
                                <textarea
                                    required
                                    rows={3}
                                    placeholder="Describe the hypothesis, MITRE techniques, IOCs, or behavioral patterns to search across logs..."
                                    value={addForm.description}
                                    onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-500"
                                />
                            </div>

                            <div className="pt-2 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsAddModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[12px] font-semibold transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-orange-600 to-indigo-600 hover:from-orange-500 hover:to-indigo-500 text-white rounded-lg text-[12px] font-bold shadow-lg transition-all disabled:opacity-50"
                                >
                                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    Save & Run Hunt
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
